import React, { useRef, useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { selectCurrentCredits } from 'src/reducers/user-limits/selector'
import Lottie from 'react-lottie'
import aiOutlineLoader from 'src/lotties/ai-outline-loader.json'
import {
  SourceDocuments,
  CourseAbout,
  TeachingGoals,
  Settings,
} from './components'
import { useAttachmentManager } from './hooks/useAttachmentManager'
import { useGoalManager } from './hooks/useGoalManager'
import { useOutlineGenerator } from './hooks/useOutlineGenerator'
import useOutlineLeaveGuard from '../course-creation-step/hooks/useOutlineLeaveGuard'
import styles from './styles'
import CourseLanguageCode from './components/course-language-code'

const WAIT_SCREEN_WARNING_DESCRIPTION =
  'If you leave now, your AI outline will be lost and you must start over.'

const PreparationStep = ({
  classes,
  onOutlineGenerated,
  documentFollowing,
  setDocumentFollowing,
  setCourseLanguageCode,
  courseLanguageCode,
}) => {
  const history = useHistory()
  const location = useLocation()
  const fileInputRef = useRef(null)
  const credits = useSelector(selectCurrentCredits)

  // The path the course is added to, when started from one.
  const collectionId = new URLSearchParams(location.search).get('collectionId')
  const loadedPath = useSelector(state => state.collection.collection)
  const pathName =
    collectionId && loadedPath?.id === collectionId ? loadedPath.name : null
  const fromPath = Boolean(collectionId) && collectionId !== 'undefined'
  const [courseAbout, setCourseAbout] = useState('')
  const [currentCharCount, setCurrentCharCount] = useState(0)
  const [userTouchedCourseAbout, setUserTouchedCourseAbout] = useState(false)

  // Get outline state from Redux store
  const outline = useSelector(state => state.aICreator.outline)
  const outlineLoading = useSelector(state => state.aICreator.outlineLoading)
  const outlineError = useSelector(state => state.aICreator.outlineError)

  useOutlineLeaveGuard(outlineLoading, WAIT_SCREEN_WARNING_DESCRIPTION)

  // Custom hooks for managing state and logic
  const {
    attachments,
    attachmentLoading,
    handleOnDropFile,
    handleFileSelect,
  } = useAttachmentManager()

  const {
    goals,
    goalsAccordionOpen,
    handleGoalsAccordionToggle,
    addNewGoal,
    updateGoal,
    handleGoalKeyPress,
    deleteGoal, // Add this function
  } = useGoalManager()

  const { settingsChangeHandler, generateOutline } = useOutlineGenerator({
    courseAbout,
    courseLanguageCode,
    attachments,
    goals,
    documentFollowing,
    onOutlineGenerated,
  })

  // Check if outline was generated and notify parent
  useEffect(() => {
    if (outline && !outlineLoading) {
      onOutlineGenerated(outline)
    }
  }, [outline, outlineLoading, onOutlineGenerated])

  const handleCourseAboutChange = event => {
    const value = event.target.value
    setCourseAbout(value)
    setCurrentCharCount(value.length)
    setUserTouchedCourseAbout(true)
  }

  // Auto-fill courseAbout from attachment name if empty.
  useEffect(() => {
    if (attachments && attachments.length > 0 && !userTouchedCourseAbout) {
      // Find the first attachment to auto-fill the topic if it's currently empty
      if (!courseAbout || courseAbout.trim() === '') {
        const firstAttachment = attachments[0]
        if (firstAttachment) {
          let title =
            firstAttachment.file_name ||
            firstAttachment.original_file_name ||
            ''

          // Do not auto-fill if the title is a URL
          if (
            title &&
            !title.startsWith('http://') &&
            !title.startsWith('https://')
          ) {
            let cleanTitle = title
            // Remove file extension if present for non-web/youtube files
            if (
              firstAttachment.attachment_type !== 'youtube' &&
              firstAttachment.attachment_type !== 'web'
            ) {
              if (cleanTitle.includes('.')) {
                cleanTitle = cleanTitle.replace(/\.[^/.]+$/, '')
              }
            }

            setCourseAbout(cleanTitle)
            setCurrentCharCount(cleanTitle.length)
          }
        }
      }
    }
  }, [attachments, courseAbout, userTouchedCourseAbout])

  const hasMinimumRequirements = () => {
    const hasBasicInfo =
      courseAbout.trim() !== '' &&
      goals.filter(goal => goal.trim() !== '').length > 0

    return hasBasicInfo
  }

  const hasAttachmentsLoading = () => {
    return attachments.some(
      attachment =>
        attachment.succeeded === false &&
        attachment.upload_status !== 'partial_failure' &&
        attachment.upload_status !== 'failed',
    )
  }

  if (outlineLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: aiOutlineLoader,
          }}
          height={238}
          width={440}
        />
      </div>
    )
  }
  const canStart =
    hasMinimumRequirements() && !attachmentLoading && !hasAttachmentsLoading()

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <button
          type="button"
          className={classes.back}
          onClick={() => history.goBack()}
        >
          <Icon icon="tabler:chevron-left" width={14} height={14} />
          {fromPath ? pathName || 'Learning path' : 'Courses'}
        </button>

        <div className={classes.header}>
          <span className={classes.headerIcon}>
            <Icon icon="tabler:sparkles" width={22} height={22} />
          </span>
          <div>
            <div className={classes.title}>AI Course Builder</div>
            <div className={classes.subtitle}>
              Set the basics and AI drafts the course for you.
              {pathName ? ` It is added to ${pathName}.` : ''}
            </div>
          </div>
        </div>

        <SourceDocuments
          fileInputRef={fileInputRef}
          handleOnDropFile={handleOnDropFile}
          handleFileSelect={handleFileSelect}
          attachmentLoading={attachmentLoading}
          documentFollowing={documentFollowing}
          onDocumentFollowingChange={setDocumentFollowing}
        />
        <CourseLanguageCode
          onCourseLanguageCodeChange={setCourseLanguageCode}
        ></CourseLanguageCode>

        <CourseAbout
          courseAbout={courseAbout}
          onCourseAboutChange={handleCourseAboutChange}
          currentCharCount={currentCharCount}
        />

        <TeachingGoals
          goals={goals}
          goalsAccordionOpen={goalsAccordionOpen}
          onGoalsAccordionToggle={handleGoalsAccordionToggle}
          onAddNewGoal={addNewGoal}
          onUpdateGoal={updateGoal}
          onGoalKeyPress={handleGoalKeyPress}
          onDeleteGoal={deleteGoal} // Pass the new function
          courseAbout={courseAbout}
          courseLanguageCode={courseLanguageCode}
          attachments={attachments}
        />

        <div className={classes.courseInfo}>
          <Settings
            settingsChangeHandler={settingsChangeHandler}
            attachments={attachments}
            documentFollowing={documentFollowing}
          />
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.footerInner}>
          <span className={classes.credits}>
            <Icon icon="tabler:coin" width={14} height={14} />
            Uses AI credits
            {credits != null &&
              ` · ${Number(credits).toLocaleString('en-US')} left`}
          </span>
          <div className={classes.footerActions}>
            <button
              type="button"
              className={classes.footerBack}
              onClick={() => history.goBack()}
            >
              Back
            </button>
            <button
              type="button"
              className={classes.start}
              style={{
                background: canStart ? '#015FFB' : '#99BFFD',
                cursor: canStart ? 'pointer' : 'default',
              }}
              disabled={!canStart}
              onClick={generateOutline}
            >
              <Icon icon="tabler:sparkles" width={14} height={14} />
              Start building
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(PreparationStep)
